import {
  BaseApiService,
  type JsonApiResponse,
  type ServiceOptions,
} from './base.service';
import { ClassTypes } from '@/shared/constants';
import type { ConceptAngleTypeType } from '@/shared/constants/concept-angle-type';
import { reconcileRendersFromJsonApi } from '@/features/design/job-queue';
import type {
  Concept,
  ConceptAngle,
  AngleStoreResponse,
  Reference,
} from '@/types/concept';
import type { DeleteConflictStrategy } from '@/types/project-angle';

export class ConceptsService extends BaseApiService {
  /**
   * Create a concept with main angle and optional derivative angles.
   * Used when creating a concept from the angle selection modal.
   */
  async createConceptWithAngles(
    projectId: string,
    mainAngleId: string,
    derivativeAngleIds: string[] = [],
    options?: ServiceOptions
  ): Promise<Concept> {
    const relationships: Record<string, unknown> = {
      main_angle: {
        data: {
          type: ClassTypes.ANGLE,
          id: mainAngleId,
        },
      },
    };

    if (derivativeAngleIds.length > 0) {
      relationships.derivative_angles = {
        data: derivativeAngleIds.map((id) => ({
          type: ClassTypes.ANGLE,
          id,
        })),
      };
    }

    const requestData = this.formatJsonApiData(
      ClassTypes.CONCEPT,
      {},
      relationships
    );

    const response = await this.post<JsonApiResponse<Concept>>(
      `/v1/projects/${projectId}/concepts`,
      requestData,
      undefined,
      {
        loading: { trackEndpoint: `concepts-create-${projectId}` },
        ...options,
      }
    );
    return response.data;
  }

  async getConcept(
    projectId: string,
    conceptId: string,
    options?: ServiceOptions
  ): Promise<Concept> {
    const response = await this.httpGet<JsonApiResponse<Concept>>(
      `/v1/projects/${projectId}/concepts/${conceptId}`,
      undefined,
      {
        loading: { trackEndpoint: `concepts-get-${conceptId}` },
        ...options,
      }
    );
    return response.data;
  }

  async getConceptWithIncludes(
    projectId: string,
    conceptId: string,
    options?: ServiceOptions
  ): Promise<JsonApiResponse<Concept>> {
    const queryString = this.buildQueryString({
      include:
        'project,angles,angles.angle,angles.angle.file,angles.renders,angles.renders.file,angles.controls,references,references.file',
    });
    const response = await this.httpGet<JsonApiResponse<Concept>>(
      `/v1/projects/${projectId}/concepts/${conceptId}?${queryString}`,
      undefined,
      {
        loading: { trackEndpoint: `concepts-get-${conceptId}` },
        ...options,
      }
    );
    // L1 reconcile: heal stale active jobs in the local queue against any
    // terminal-status renders returned from this API call. See WEB-L1 in
    // docs/REALTIME_RECONCILE_PLAN.md.
    reconcileRendersFromJsonApi(response.included);
    return response;
  }

  async updateConcept(
    projectId: string,
    conceptId: string,
    updates: {
      title?: string | null;
      description?: string | null;
      note?: string | null;
      is_favorite?: boolean;
    },
    options?: ServiceOptions
  ): Promise<Concept> {
    const requestData = this.formatJsonApiData(
      ClassTypes.CONCEPT,
      updates,
      undefined,
      conceptId
    );

    const response = await this.put<JsonApiResponse<Concept>>(
      `/v1/projects/${projectId}/concepts/${conceptId}`,
      requestData,
      undefined,
      {
        loading: { trackEndpoint: `concepts-update-${conceptId}` },
        ...options,
      }
    );
    return response.data;
  }

  async deleteConcept(
    projectId: string,
    conceptId: string,
    options?: ServiceOptions
  ): Promise<void> {
    await this.httpDelete(
      `/v1/projects/${projectId}/concepts/${conceptId}`,
      undefined,
      {
        loading: { trackEndpoint: `concepts-delete-${conceptId}` },
        ...options,
      }
    );
  }

  async restoreConcept(projectId: string, conceptId: string): Promise<void> {
    await this.post(
      `/v1/projects/${projectId}/concepts/${conceptId}/restore`
    );
  }

  async listConcepts(
    projectId: string,
    params?: { page?: number; pageSize?: number },
    options?: ServiceOptions
  ): Promise<Concept[]> {
    const queryParts: string[] = [];
    if (params?.page) queryParts.push(`page[number]=${params.page}`);
    if (params?.pageSize) queryParts.push(`page[size]=${params.pageSize}`);
    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

    const response = await this.httpGet<JsonApiResponse<Concept[]>>(
      `/v1/projects/${projectId}/concepts${queryString}`,
      undefined,
      {
        loading: { trackEndpoint: `concepts-list-${projectId}` },
        ...options,
      }
    );
    return response.data;
  }

  async getConceptAngles(
    conceptId: string,
    options?: ServiceOptions
  ): Promise<{ data: ConceptAngle[]; included?: unknown[] }> {
    const queryString = this.buildQueryString({
      include: 'angle.file,renders,latestRender',
    });
    const response = await this.httpGet<JsonApiResponse<ConceptAngle[]>>(
      `/v1/concepts/${conceptId}/angles?${queryString}`,
      undefined,
      {
        loading: { trackEndpoint: `concept-angles-${conceptId}` },
        ...options,
      }
    );
    // L1 reconcile (WEB-L1) — see getConceptWithIncludes for rationale.
    reconcileRendersFromJsonApi(response.included);
    return { data: response.data, included: response.included };
  }

  async createAngleForConcept(
    conceptId: string,
    fileName: string,
    md5: string,
    contentType: string,
    angleType?: ConceptAngleTypeType,
    width?: number,
    height?: number,
    aspectRatio?: string,
    options?: ServiceOptions,
    // STRICT-mode intake (concept "add view" — the "+" next to Main/Second view).
    // When 'view', the server does NOT auto-route a different-building image to
    // References (lenient Brief behaviour); it hard-blocks it so the user must
    // confirm "It's my building" or cancel. Omitted on lenient Brief intake.
    intent?: 'view'
  ): Promise<AngleStoreResponse> {
    const attributes: Record<string, unknown> = {
      file_name: fileName,
      md5,
      content_type: contentType,
      angle_type: angleType,
    };
    if (width) attributes.width = width;
    if (height) attributes.height = height;
    if (aspectRatio) attributes.aspect_ratio = aspectRatio;
    if (intent) attributes.intent = intent;

    const requestData = this.formatJsonApiData('angle', attributes);

    const response = await this.post<JsonApiResponse<AngleStoreResponse>>(
      `/v1/concepts/${conceptId}/angles`,
      requestData,
      undefined,
      {
        loading: { trackEndpoint: `angles-create-${conceptId}` },
        ...options,
      }
    );
    return response.data;
  }

  async syncConceptAngles(
    conceptId: string,
    items: Array<{ angle_id: string; type: 'main' | 'derivative' }>,
    options?: ServiceOptions
  ): Promise<ConceptAngle[]> {
    const requestData = {
      data: {
        type: ClassTypes.CONCEPT_ANGLES,
        attributes: {
          angles: items,
        },
      },
    };

    const response = await this.put<JsonApiResponse<ConceptAngle[]>>(
      `/v1/concepts/${conceptId}/angles/sync`,
      requestData,
      undefined,
      {
        loading: { trackEndpoint: `angles-sync-${conceptId}` },
        ...options,
      }
    );
    return response.data;
  }

  /**
   * Delete a shared view from THIS concept. Removal fans out across the whole
   * project (the view set is shared). Two-phase conflict protocol: without
   * `strategy` the server may answer 409 ANGLE_DELETE_CONFLICT (the view has
   * renders in — or is the main of — other concepts) — the caller shows
   * AngleDeleteConflictDialog and retries with the chosen `strategy` (phase 2).
   * `conceptAngleId` is the concept_angle pivot id the endpoint expects.
   */
  async deleteConceptAngle(
    conceptId: string,
    conceptAngleId: string,
    strategy?: DeleteConflictStrategy,
    options?: ServiceOptions
  ): Promise<void> {
    const query = strategy ? `?conflict_strategy=${strategy}` : '';
    await this.httpDelete(
      `/v1/concepts/${conceptId}/angles/${conceptAngleId}${query}`,
      undefined,
      {
        loading: { trackEndpoint: `angles-delete-${conceptAngleId}` },
        ...options,
      }
    );
  }

  async replaceDerivativeAngle(
    conceptId: string,
    conceptAngleId: string,
    fileName: string,
    md5: string,
    contentType: string,
    width?: number,
    height?: number,
    aspectRatio?: string,
    options?: ServiceOptions
  ): Promise<AngleStoreResponse> {
    const requestData = this.formatJsonApiData('angle', {
      file_name: fileName,
      md5,
      content_type: contentType,
      ...(width !== undefined && { width }),
      ...(height !== undefined && { height }),
      ...(aspectRatio !== undefined && { aspect_ratio: aspectRatio }),
      // Strict: replacing a concept view must stay a view (no auto-route). Same
      // payload on all frontends.
      intent: 'view',
    });

    const response = await this.put<JsonApiResponse<AngleStoreResponse>>(
      `/v1/concepts/${conceptId}/angles/${conceptAngleId}/replace`,
      requestData,
      undefined,
      {
        loading: { trackEndpoint: `angles-replace-${conceptAngleId}` },
        ...options,
      }
    );
    return response.data;
  }

  async getConceptReferences(
    conceptId: string,
    options?: ServiceOptions
  ): Promise<Reference[]> {
    const response = await this.httpGet<JsonApiResponse<Reference[]>>(
      `/v1/concepts/${conceptId}/references`,
      undefined,
      {
        loading: { trackEndpoint: `concept-references-${conceptId}` },
        ...options,
      }
    );
    return response.data;
  }

  /**
   * Swap a concept angle with a different project angle (atomic operation).
   * Preserves angle type (main/derivative) and order.
   */
  async swapConceptAngle(
    conceptId: string,
    oldConceptAngleId: string,
    newAngleId: string,
    options?: ServiceOptions
  ): Promise<ConceptAngle> {
    const requestData = this.formatJsonApiData('concept_angles', {
      old_concept_angle_id: oldConceptAngleId,
      new_angle_id: newAngleId,
    });

    const response = await this.post<JsonApiResponse<ConceptAngle>>(
      `/v1/concepts/${conceptId}/angles/swap`,
      requestData,
      undefined,
      {
        loading: { trackEndpoint: `angles-swap-${oldConceptAngleId}` },
        ...options,
      }
    );
    return response.data;
  }
}

export const conceptsService = new ConceptsService();
